import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './Auth';
import { useNavigate, useLocation } from 'react-router-dom';
import CirclePack from './Holacracy/CirclePack';
import { Plus, RefreshCcw } from 'lucide-react';
import TensionEditor from './Holacracy/TensionEditor';
import TensionsPanel from './Holacracy/TensionsPanel';
import PointsPanel from './Holacracy/PointsPanel';
import MeetingPanel from './Holacracy/MeetingPanel';
import MeetingHistoryModal from './Holacracy/MeetingHistoryModal';
import VotesPanel from './Holacracy/VotesPanel';
import StructurePanel from './Holacracy/StructurePanel';
import CircleInsightsPanel from './Holacracy/CircleInsightsPanel';
import RulesPanel from './Holacracy/RulesPanel';
import { OrgMentionsContext } from './Holacracy/structure/focus';
import useMeetingState from './Holacracy/hooks/useMeetingState';
import { apiService } from '../services/api';
import { circlePatch } from './Holacracy/structure/patch';

// Inline editing helpers (one field at a time)
const sanitizeList = (list) =>
  Array.isArray(list) ? list.map((v) => (v || '').trim()).filter(Boolean) : [];

const getArrayField = (value, { stringFallback = false } = {}) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') return [];
  const trimmed = value.trim();
  if (!trimmed) return [];
  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) return parsed;
    if (stringFallback && typeof parsed === 'string' && parsed.trim()) {
      return [parsed.trim()];
    }
    return [];
  } catch {
    return stringFallback ? [trimmed] : [];
  }
};

const normalizeStringListField = (value) =>
  getArrayField(value, { stringFallback: true })
    .map((item) => (typeof item === 'string' ? item : String(item ?? '')))
    .map((item) => item.trim())
    .filter(Boolean);

const normalizeStrategyField = (value) =>
  getArrayField(value)
    .map((item) => {
      if (typeof item === 'string') {
        const content = item.trim();
        return content ? { content } : null;
      }
      return item && typeof item === 'object' ? item : null;
    })
    .filter(Boolean);

const normalizeOwnerIds = (ownerIds, ownerId) => {
  let source = [];
  if (Array.isArray(ownerIds)) {
    source = ownerIds;
  } else if (typeof ownerIds === 'string') {
    const trimmed = ownerIds.trim();
    if (trimmed) {
      try {
        const parsed = JSON.parse(trimmed);
        source = Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        source = [trimmed];
      }
    }
  }

  const ids = source
    .filter((id) => id !== null && id !== undefined && id !== '')
    .map((id) => Number(id))
    .filter((id) => !Number.isNaN(id));
  const primary = Number(ownerId);
  if (ownerId !== null && ownerId !== undefined && ownerId !== '' && !Number.isNaN(primary)) {
    ids.unshift(primary);
  }
  return Array.from(new Set(ids));
};

const normalizeCircle = (circle) => {
  if (!circle) return circle;
  const rawTimeout = Number(circle.vote_timeout_days || 0);
  const vote_timeout_days = Number.isNaN(rawTimeout) || rawTimeout < 1 ? 3 : rawTimeout;
  return {
    ...circle,
    domains: normalizeStringListField(circle.domains),
    accountabilities: normalizeStringListField(circle.accountabilities),
    strategy: normalizeStrategyField(circle.strategy),
    owner_ids: normalizeOwnerIds(circle.owner_ids, circle.owner_id),
    reserved_domains: Array.isArray(circle.reserved_domains) ? circle.reserved_domains : [],
    vote_timeout_days,
  };
};

const readError = async (response, fallback) => {
  try {
    const data = await response.json();
    if (typeof data?.detail === 'string') return data.detail;
  } catch {
    // pas de JSON
  }
  return fallback;
};

const buildEntitySignature = (items, fields = ['id']) => {
  if (!Array.isArray(items)) return '';
  return items
    .map((item) =>
      fields.map((field) => String(item?.[field] ?? '')).join(':')
    )
    .sort()
    .join('|');
};

const focusMentionPattern = /@\[(circle|role|project|rule):(\d+)\|([^\]]+)\]/g;

const formatFocusForExport = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.replace(focusMentionPattern, (_match, _type, _id, label) => `@${label}`);
};

const nullableText = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
};

const slugifyFilenamePart = (value, fallback = 'organisation') => {
  const slug = String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || fallback;
};

const downloadJsonFile = (payload, filename) => {
  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: 'application/json;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const HolacracyPage = () => {
  const DAILY_HOURS = 7.8;
  const [circles, setCircles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedCircle, setSelectedCircle] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [superEditMode, setSuperEditMode] = useState(false);
  const [newCircleParentId, setNewCircleParentId] = useState(null);
  const [structureError, setStructureError] = useState(null);
  const [dismissedMeetingError, setDismissedMeetingError] = useState(null);
  const [activeTab, setActiveTab] = useState('structure'); // structure | tensions | points | votes | meeting
  const [projects, setProjects] = useState([]);
  const [projectSprints, setProjectSprints] = useState({});
  const [slots, setSlots] = useState([]);
  const [tensions, setTensions] = useState([]);
  const [circlePoints, setCirclePoints] = useState([]);
  const [circlePointsLoading, setCirclePointsLoading] = useState(false);
  const [circlePointsError, setCirclePointsError] = useState(null);
  const [tensionDraft, setTensionDraft] = useState(null);
  const [selectedTensionId, setSelectedTensionId] = useState(null);
  const [tensionsLoading, setTensionsLoading] = useState(false);
  const [tensionError, setTensionError] = useState(null);
  const [votes, setVotes] = useState([]);
  const [votesLoading, setVotesLoading] = useState(false);
  const [votesError, setVotesError] = useState(null);
  const [tensionVotes, setTensionVotes] = useState([]);
  const [tensionVotesLoading, setTensionVotesLoading] = useState(false);
  const [showTensionModal, setShowTensionModal] = useState(false);
  const [roleFocusMap, setRoleFocusMap] = useState({});
  const [organizationExporting, setOrganizationExporting] = useState(false);
  const [projectMetricsById, setProjectMetricsById] = useState({});
  const [projectMetricsLoading, setProjectMetricsLoading] = useState({});
  const [projectMetricsError, setProjectMetricsError] = useState(null);
  const [metricsRange, setMetricsRange] = useState({ start: '', end: '' });
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const circlesLoadedRef = useRef(false);
  const lowPowerDevice = React.useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    const cores = Number(navigator.hardwareConcurrency || 0);
    const memory = Number(navigator.deviceMemory || 0);
    return (
      (Number.isFinite(cores) && cores > 0 && cores <= 4) ||
      (Number.isFinite(memory) && memory > 0 && memory <= 4)
    );
  }, []);
  const projectMetricsByIdRef = useRef({});
  const projectMetricsLoadingRef = useRef({});
  const projectMetricsRequestsRef = useRef({});
  const isMountedRef = useRef(true);
  const structureChangedRef = useRef(null);
  const tensionsChangedRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const effectiveUserId = React.useMemo(() => user?.employee_id ?? user?.id ?? null, [user]);
  const meetingIdFromQuery = React.useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return params.get('meeting');
  }, [location.search]);
  const meetingIdFromState = React.useMemo(() => {
    const stateMeetingId = location.state?.resumeMeetingId;
    if (stateMeetingId === null || stateMeetingId === undefined || stateMeetingId === '') return null;
    return String(stateMeetingId);
  }, [location.state]);
  const pendingResumeMeetingId = meetingIdFromState || meetingIdFromQuery;
  const tabFromQuery = React.useMemo(() => {
    const asked = new URLSearchParams(location.search || '').get('tab');
    return ['structure', 'insights', 'rules', 'tensions', 'points', 'votes'].includes(asked) ? asked : null;
  }, [location.search]);
  const isSuperAdmin = React.useMemo(
    () => Number(user?.employee_id ?? user?.id) === 1,
    [user]
  );
  const isAdminByTag = React.useMemo(() => {
    const employeeId = user?.employee_id;
    if (!employeeId) return Boolean(user?.is_admin);
    const employee = employees.find((emp) => Number(emp.id) === Number(employeeId));
    if (!employee) return Boolean(user?.is_admin);
    return Boolean(employee.is_admin);
  }, [employees, user]);
  const isSuperEditor = isSuperAdmin || isAdminByTag;

  const API_URL = apiService.getBaseUrl();

  useEffect(() => {
    if (tabFromQuery) setActiveTab(tabFromQuery);
  }, [tabFromQuery]);

  useEffect(() => {
    if (!isSuperEditor && superEditMode) {
      setSuperEditMode(false);
      setIsEditing(false);
    }
  }, [isSuperEditor, superEditMode]);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  useEffect(() => {
    projectMetricsByIdRef.current = projectMetricsById;
  }, [projectMetricsById]);
  useEffect(() => {
    projectMetricsLoadingRef.current = projectMetricsLoading;
  }, [projectMetricsLoading]);

  const normalizeTension = useCallback((tension) => {
    if (!tension) return tension;
    const action = tension.action || (tension.proposed_data?.delete ? 'delete' : 'edit');
    let operations = tension.operations || [];
    if (!Array.isArray(operations) && typeof operations === 'string') {
      try {
        operations = JSON.parse(operations);
      } catch (err) {
        operations = [];
      }
    }
    if (!Array.isArray(operations)) operations = [];
    return { ...tension, action, operations };
  }, []);

  const {
    activeMeeting,
    meetingTensions,
    meetingParticipants,
    meetingNotes,
    meetingPresenceCount,
    meetingConnectedUsers,
    meetingRealtimeConnected,
    meetingFormVisible,
    meetingPoints,
    meetingPreparedPoints,
    meetingPreparedPointsLoading,
    selectedMeetingPointId,
    selectedMeetingPointIdShared,
    selectedMeetingProjectId,
    selectedMeetingProjectIdShared,
    meetingProjectReviews,
    meetingPointComposer,
    meetingLoading,
    meetingError,
    meetingHistory,
    meetingHistoryLoading,
    meetingHistoryError,
    showMeetingHistory,
    meetingPhase,
    meetingDraft,
    selectedMeetingTensionId,
    selectedMeetingTensionIdShared,
    openMeetings,
    openMeetingsByCircle,
    openMeetingCircleIds,
    fetchMeetingTensions,
    fetchMeetingProjectReviews,
    handleMeetingDraftChange,
    handleTransferSecretary,
    handleMeetingNotesChange,
    handleOpenMeeting,
    handleCloseMeeting,
    handleOpenMeetingHistory,
    handleCloseMeetingHistory,
    handleImportMeetingTension,
    handleCreateMeetingTension,
    handleJoinMeeting,
    handleToggleMeetingForm,
    handleAddMeetingPoint,
    handleImportMeetingPoint,
    handleSelectMeetingPoint,
    handleMeetingPointDecision,
    handleMeetingPointToTension,
    handleMeetingPointNotesChange,
    handleMeetingPointComposerUpdate,
    handleSelectMeetingProject,
    handleSelectMeetingPhase,
    handleUpdateMeetingProjectReview,
    handleCreateTacticalTask,
    handleCreateTacticalObjective,
    handleInviteMeetingParticipant,
    handleLiveMeetingTensionEdit,
    handleSelectMeetingTension,
    handleMeetingDecision,
    clearMeetingError,
    meetingLive,
    liveActions,
    meetingFocus,
    meetingFocusList,
    focusActions,
  } = useMeetingState({
    apiUrl: API_URL,
    getAuthHeaders,
    user,
    selectedCircle,
    setSelectedCircle,
    activeTab,
    setActiveTab,
    circles,
    normalizeTension,
    // Définies plus bas (fetchCircles, fetchTensions) : appelées par référence.
    onStructureChanged: () => structureChangedRef.current?.(),
    onTensionsChanged: () => tensionsChangedRef.current?.(),
  });

  const fetchCircles = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/circles`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch circles');
      const data = await response.json();
      const normalized = (data || []).map(normalizeCircle);
      // Structure inchangée : même objet, la carte n'est pas redessinée pour rien.
      setCircles((prev) => (JSON.stringify(prev) === JSON.stringify(normalized) ? prev : normalized));
      circlesLoadedRef.current = true;
      setError(null);
    } catch (err) {
      // Une fois la page affichée, un rechargement raté (pendant une réunion…) ne la remplace pas par une erreur.
      if (circlesLoadedRef.current) setStructureError('Rechargement de la structure impossible, réessaie dans un instant.');
      else setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [API_URL, getAuthHeaders]);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/employees`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch employees');
      const data = await response.json();
      setEmployees(data);
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  }, [API_URL, getAuthHeaders]);

  // Règles de l'organisation : citées dans les redevabilités et domaines (« @ »), lues et écrites dans l'onglet Règles.
  const [orgRules, setOrgRules] = useState([]);
  const [canEditRules, setCanEditRules] = useState(false);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesError, setRulesError] = useState(null);
  const [focusRuleId, setFocusRuleId] = useState(null);
  const fetchRules = useCallback(async () => {
    setRulesLoading(true);
    try {
      const data = await apiService.get('/org-rules');
      setOrgRules(Array.isArray(data?.rules) ? data.rules : []);
      setCanEditRules(Boolean(data?.can_edit));
      setRulesError(null);
    } catch (err) {
      setRulesError(err?.message || 'Règles indisponibles');
    } finally {
      setRulesLoading(false);
    }
  }, []);
  const createRule = useCallback(async (fields) => {
    const created = await apiService.post('/org-rules', fields);
    await fetchRules();
    setFocusRuleId(created?.id ?? null);
  }, [fetchRules]);
  const updateRule = useCallback(async (ruleId, fields) => {
    await apiService.put(`/org-rules/${ruleId}`, fields);
    await fetchRules();
    fetchCircles(); // un nom changé est repris dans les mentions des redevabilités et domaines
  }, [fetchRules, fetchCircles]);
  const deleteRule = useCallback(async (ruleId) => {
    await apiService.delete(`/org-rules/${ruleId}`);
    await fetchRules();
  }, [fetchRules]);

  const fetchProjects = useCallback(async () => {
    try {
      let response = await fetch(`${API_URL}/projects/options`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        response = await fetch(`${API_URL}/projects`, {
          headers: getAuthHeaders(),
        });
      }
      if (!response.ok) throw new Error('Failed to fetch projects');
      const data = await response.json();
      const next = Array.isArray(data) ? data : [];
      setProjects((prev) => {
        const prevSig = buildEntitySignature(prev, ['id', 'name', 'project_number', 'status', 'circle_id']);
        const nextSig = buildEntitySignature(next, ['id', 'name', 'project_number', 'status', 'circle_id']);
        return prevSig === nextSig ? prev : next;
      });
    } catch (err) {
      console.error('Error fetching projects:', err);
    }
  }, [API_URL, getAuthHeaders]);

  const fetchSlots = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/slots`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch slots');
      const data = await response.json();
      const next = Array.isArray(data) ? data : [];
      setSlots((prev) => {
        const prevSig = buildEntitySignature(prev, ['id', 'name', 'start_date', 'end_date']);
        const nextSig = buildEntitySignature(next, ['id', 'name', 'start_date', 'end_date']);
        return prevSig === nextSig ? prev : next;
      });
    } catch (err) {
      console.error('Error fetching slots:', err);
      setSlots([]);
    }
  }, [API_URL, getAuthHeaders]);

  const fetchProjectMetrics = useCallback(
    async (projectId) => {
      if (!projectId) return [];
      const key = String(projectId);
      if (projectMetricsRequestsRef.current[key]) {
        return projectMetricsRequestsRef.current[key];
      }
      const requestPromise = (async () => {
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), 30000);
        try {
          const response = await fetch(`${API_URL}/projects/${projectId}/metrics`, {
            headers: getAuthHeaders(),
            signal: controller.signal,
          });
          if (!response.ok) throw new Error('Failed to fetch project metrics');
          const data = await response.json();
          return Array.isArray(data) ? data : [];
        } catch (err) {
          if (err?.name === 'AbortError') {
            throw new Error('Timeout while fetching project metrics');
          }
          throw err;
        } finally {
          window.clearTimeout(timeoutId);
        }
      })();
      projectMetricsRequestsRef.current[key] = requestPromise;
      try {
        return await requestPromise;
      } finally {
        if (projectMetricsRequestsRef.current[key] === requestPromise) {
          delete projectMetricsRequestsRef.current[key];
        }
      }
    },
    [API_URL, getAuthHeaders]
  );

  const fetchProjectObjectives = useCallback(
    async (projectId) => {
      if (!projectId) return [];
      const response = await fetch(`${API_URL}/projects/${projectId}/objectives`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch project objectives');
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    },
    [API_URL, getAuthHeaders]
  );

  const buildFocusKey = (circleId, employeeId) => `${circleId}:${employeeId}`;

  // Tous les focus d'un coup (petite table) : fiche d'une personne et portée des domaines à effet.
  const fetchRoleFocus = useCallback(
    async () => {
      try {
        const response = await fetch(`${API_URL}/circle-focus`, {
          headers: getAuthHeaders(),
        });
        if (!response.ok) throw new Error('Failed to fetch role focus');
        const data = await response.json();
        const next = {};
        (data || []).forEach((item) => {
          next[buildFocusKey(item.circle_id, item.employee_id)] = item.focus_text || '';
        });
        setRoleFocusMap(next);
      } catch (err) {
        console.error('Error fetching role focus:', err);
      }
    },
    [API_URL, getAuthHeaders]
  );

  const fetchProjectSprints = useCallback(
    async (projectId) => {
      if (!projectId) return;
      const key = String(projectId);
      if (projectSprints[key]) return;
      try {
        const response = await fetch(`${API_URL}/projects/${projectId}/sprints`, {
          headers: getAuthHeaders(),
        });
        if (!response.ok) throw new Error('Failed to fetch sprints');
        const data = await response.json();
        setProjectSprints((prev) => ({ ...prev, [key]: data || [] }));
      } catch (err) {
        console.error('Error fetching sprints:', err);
      }
    },
    [API_URL, getAuthHeaders, projectSprints]
  );


  const fetchTensions = useCallback(async () => {
    try {
      setTensionsLoading(true);
      const response = await fetch(`${API_URL}/circle-tensions`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error(await readError(response, 'Impossible de récupérer les tensions.'));
      const data = await response.json();
      const normalized = (data || []).map(normalizeTension);
      setTensions(normalized);
      if (selectedTensionId) {
        const latest = normalized.find((t) => String(t.id) === String(selectedTensionId));
        if (!latest) {
          setSelectedTensionId(null);
          setTensionDraft(null);
        } else {
          setTensionDraft((prev) => (prev && String(prev.id) === String(latest.id) && prev.status !== latest.status ? latest : prev));
        }
      }
      setTensionError(null);
    } catch (err) {
      setTensionError(err.message);
    } finally {
      setTensionsLoading(false);
    }
  }, [API_URL, getAuthHeaders, selectedTensionId, normalizeTension]);

  structureChangedRef.current = fetchCircles;
  tensionsChangedRef.current = fetchTensions;

  const fetchCirclePoints = useCallback(async (circleIdOverride) => {
    const circleId = circleIdOverride || selectedCircle?.id;
    if (!circleId) {
      setCirclePoints([]);
      return;
    }
    try {
      setCirclePointsLoading(true);
      const response = await fetch(`${API_URL}/circle-points?circle_id=${circleId}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Impossible de recuperer les points');
      const data = await response.json();
      setCirclePoints(Array.isArray(data) ? data : []);
      setCirclePointsError(null);
    } catch (err) {
      setCirclePointsError(err.message);
      setCirclePoints([]);
    } finally {
      setCirclePointsLoading(false);
    }
  }, [API_URL, getAuthHeaders, selectedCircle?.id]);

  const handleCreateCirclePoint = useCallback(async (title) => {
    const trimmed = (title || '').trim();
    if (!trimmed || !selectedCircle?.id) return null;
    try {
      const response = await fetch(`${API_URL}/circle-points`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ circle_id: selectedCircle.id, title: trimmed }),
      });
      if (!response.ok) throw new Error("Impossible d'ajouter le point");
      const created = await response.json();
      fetchCirclePoints(selectedCircle.id);
      return created;
    } catch (err) {
      setCirclePointsError(err.message);
      return null;
    }
  }, [API_URL, getAuthHeaders, selectedCircle?.id, fetchCirclePoints]);

  const handleDeleteCirclePoint = useCallback(async (pointId) => {
    if (!pointId) return;
    if (!window.confirm('Supprimer ce point ?')) return;
    try {
      const response = await fetch(`${API_URL}/circle-points/${pointId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Suppression impossible');
      fetchCirclePoints(selectedCircle?.id);
    } catch (err) {
      setCirclePointsError(err.message);
    }
  }, [API_URL, getAuthHeaders, selectedCircle?.id, fetchCirclePoints]);

  const fetchTensionVotes = useCallback(
    async (tensionId) => {
      if (!tensionId) {
        setTensionVotes([]);
        return;
      }
      try {
        setTensionVotesLoading(true);
        const response = await fetch(`${API_URL}/circle-votes/tension/${tensionId}`, {
          headers: getAuthHeaders(),
        });
        if (!response.ok) throw new Error('Impossible de recuperer les votes');
        const data = await response.json();
        setTensionVotes(data || []);
      } catch (err) {
        setTensionVotes([]);
      } finally {
        setTensionVotesLoading(false);
      }
    },
    [API_URL, getAuthHeaders]
  );

  useEffect(() => {
    if (tensionDraft?.id && tensionDraft.status === 'proposal') {
      fetchTensionVotes(tensionDraft.id);
    } else {
      setTensionVotes([]);
    }
  }, [tensionDraft?.id, tensionDraft?.status, fetchTensionVotes]);

  const fetchVotes = useCallback(async (options = {}) => {
    const { silent = false } = options;
    try {
      if (!silent) setVotesLoading(true);
      const response = await fetch(`${API_URL}/circle-votes/pending`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Impossible de recuperer les votes');
      const data = await response.json();
      const nextVotes = Array.isArray(data) ? data : [];
      setVotes((prev) => {
        const prevSig = buildEntitySignature(prev, ['id', 'status', 'updated_at', 'created_at']);
        const nextSig = buildEntitySignature(nextVotes, ['id', 'status', 'updated_at', 'created_at']);
        return prevSig === nextSig ? prev : nextVotes;
      });
      setVotesError(null);
    } catch (err) {
      setVotesError(err.message);
    } finally {
      if (!silent) setVotesLoading(false);
    }
  }, [API_URL, getAuthHeaders]);

  useEffect(() => {
    fetchCircles();
    fetchEmployees();
    let timeoutId = null;
    let idleId = null;
    const fetchSupportData = () => {
      fetchProjects();
      fetchSlots();
      fetchRules();
    };
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(fetchSupportData, { timeout: 1200 });
    } else {
      timeoutId = window.setTimeout(fetchSupportData, 120);
    }
    return () => {
      if (idleId !== null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [fetchCircles, fetchEmployees, fetchProjects, fetchSlots, fetchRules]);

  useEffect(() => {
    if (activeTab === 'tensions' || activeTab === 'meeting') {
      fetchTensions();
    }
    if (activeTab === 'points') {
      fetchCirclePoints();
    }
    if (activeTab === 'votes') {
      fetchVotes();
    }
    if (activeTab === 'rules') {
      fetchRules(); // où elles sont citées change avec la gouvernance
    }
  }, [activeTab, fetchTensions, fetchCirclePoints, fetchVotes, fetchRules]);

  useEffect(() => {
    let active = true;
    const refreshVotes = async () => {
      if (!active) return;
      await fetchVotes({ silent: true });
    };
    refreshVotes();
    const interval = setInterval(refreshVotes, 20000);
    const handleFocus = () => refreshVotes();
    window.addEventListener('focus', handleFocus);
    return () => {
      active = false;
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchVotes]);

  const childrenByParent = React.useMemo(() => {
    const map = {};
    circles.forEach((c) => {
      if (!c.parent_id) return;
      if (!map[c.parent_id]) map[c.parent_id] = [];
      map[c.parent_id].push(c);
    });
    return map;
  }, [circles]);

  const handleExportOrganization = useCallback(async () => {
    if (!Array.isArray(circles) || circles.length === 0) {
      setStructureError('Aucune organisation à exporter.');
      return;
    }
    setOrganizationExporting(true);
    try {
      const response = await fetch(`${API_URL}/circle-focus`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Impossible de récupérer les focus des rôles');
      const focusRows = await response.json();
      const focusByKey = {};
      (focusRows || []).forEach((item) => {
        if (!item?.circle_id || !item?.employee_id) return;
        focusByKey[buildFocusKey(item.circle_id, item.employee_id)] = item.focus_text || '';
      });

      let employeeList = employees || [];
      if (employeeList.length === 0) {
        const employeeResponse = await fetch(`${API_URL}/employees`, {
          headers: getAuthHeaders(),
        });
        if (employeeResponse.ok) {
          const employeeData = await employeeResponse.json();
          employeeList = Array.isArray(employeeData) ? employeeData : [];
        }
      }
      const employeeById = new Map();
      employeeList.forEach((employee) => {
        if (employee?.id !== undefined && employee?.id !== null) {
          employeeById.set(Number(employee.id), employee);
        }
      });
      const circleById = new Map((circles || []).map((circle) => [Number(circle.id), circle]));
      const buildLeaders = (circle) =>
        normalizeOwnerIds(circle?.owner_ids, circle?.owner_id).map((id) => {
          const employee = employeeById.get(Number(id));
          return {
            id,
            nom: employee?.name || null,
            email: employee?.email || null,
            focus: formatFocusForExport(focusByKey[buildFocusKey(circle.id, id)]),
          };
        });

      const buildNode = (circle) => {
        const children = childrenByParent[circle.id] || [];
        const isRole = Boolean(circle.parent_id) && children.length === 0;
        return {
          id: circle.id,
          type: isRole ? 'role' : 'cercle',
          nom: nullableText(circle.name),
          vision: nullableText(circle.vision),
          raison_etre: nullableText(circle.purpose),
          redevabilites: sanitizeList(circle.accountabilities),
          leaders: buildLeaders(circle),
          enfants: children.map(buildNode),
        };
      };

      const roots = (circles || []).filter(
        (circle) => !circle.parent_id || !circleById.has(Number(circle.parent_id))
      );
      const rootNode =
        roots.length === 1
          ? buildNode(roots[0])
          : {
              id: null,
              type: 'organisation',
              nom: 'Organization',
              vision: null,
              raison_etre: null,
              redevabilites: [],
              leaders: [],
              enfants: roots.map(buildNode),
            };
      const exportPayload = {
        format: 'holacracy-organization-tree-v1',
        exported_at: new Date().toISOString(),
        organisation: rootNode,
      };
      const filenameRoot = slugifyFilenamePart(rootNode.nom, 'organisation');
      const datePart = new Date().toISOString().slice(0, 10);
      downloadJsonFile(exportPayload, `holacratie_${filenameRoot}_${datePart}.json`);
    } catch (err) {
      setStructureError(err?.message || "Erreur lors de l'export JSON.");
    } finally {
      setOrganizationExporting(false);
    }
  }, [API_URL, childrenByParent, circles, employees, getAuthHeaders]);

  const parseDateInput = useCallback((value) => {
    if (!value) return null;
    if (value instanceof Date) return new Date(value);
    if (typeof value === 'string') {
      const trimmed = value.split('T')[0];
      const [year, month, day] = trimmed.split('-').map(Number);
      if (year && month && day) {
        return new Date(year, month - 1, day);
      }
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed;
  }, []);

  const formatDateInput = useCallback(
    (value) => {
      const parsed = parseDateInput(value);
      if (!parsed) return '';
      return parsed.toLocaleDateString('sv-SE');
    },
    [parseDateInput]
  );

  const currentSlot = React.useMemo(() => {
    if (!Array.isArray(slots) || slots.length === 0) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return (
      slots.find((slot) => {
        const start = parseDateInput(slot.start_date);
        const end = parseDateInput(slot.end_date || slot.start_date);
        if (!start || !end) return false;
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        return today >= start && today <= end;
      }) || null
    );
  }, [slots, parseDateInput]);

  const currentSlotRange = React.useMemo(() => {
    if (!currentSlot) return null;
    const start = formatDateInput(currentSlot.start_date);
    const end = formatDateInput(currentSlot.end_date || currentSlot.start_date);
    if (!start || !end) return null;
    return {
      start,
      end,
      label: currentSlot.name || `Slot ${currentSlot.id || ''}`.trim(),
    };
  }, [currentSlot, formatDateInput]);

  useEffect(() => {
    if (!currentSlotRange) return;
    setMetricsRange((prev) => {
      if (prev.start || prev.end) return prev;
      return { start: currentSlotRange.start, end: currentSlotRange.end };
    });
  }, [currentSlotRange]);

  const hierarchyData = React.useMemo(() => {
    const buildHierarchy = (flatList) => {
      if (!flatList || flatList.length === 0) return null;
      const map = {};
      const roots = [];
      flatList.forEach((item) => {
        map[item.id] = { ...item, children: [] };
      });
      flatList.forEach((item) => {
        if (item.parent_id && map[item.parent_id]) {
          map[item.parent_id].children.push(map[item.id]);
        } else {
          roots.push(map[item.id]);
        }
      });
      if (roots.length > 1) return { name: 'Organization', children: roots };
      return roots[0] || { name: 'Organization', children: [] };
    };
    return buildHierarchy(circles);
  }, [circles]);

  const circleMemberIds = React.useMemo(() => {
    if (!selectedCircle?.id) return [];
    const ids = new Set();
    const addId = (val) => {
      if (val === null || val === undefined || val === '') return;
      const num = Number(val);
      if (!Number.isNaN(num)) ids.add(num);
    };
    (selectedCircle.owner_ids || []).forEach(addId);
    addId(selectedCircle.owner_id);
    const directRoles = childrenByParent[selectedCircle.id] || [];
    directRoles.forEach((role) => {
      (role.owner_ids || []).forEach(addId);
      addId(role.owner_id);
    });
    return Array.from(ids);
  }, [selectedCircle, childrenByParent]);

  useEffect(() => {
    if (!selectedCircle && !pendingResumeMeetingId && hierarchyData && !loading && !isEditing) {
      if (hierarchyData.id) {
        setSelectedCircle(hierarchyData);
      } else if (hierarchyData.children && hierarchyData.children.length > 0) {
        setSelectedCircle(hierarchyData.children[0]);
      }
    }
  }, [pendingResumeMeetingId, hierarchyData, loading, selectedCircle, isEditing]);

  useEffect(() => {
    if (!selectedCircle?.id || isEditing) return;
    const latest = circles.find((circle) => String(circle?.id) === String(selectedCircle.id));
    if (!latest) {
      // Cercle supprimé (gouvernance, autre onglet) : on ne garde pas une fiche fantôme.
      if (circles.length > 0) setSelectedCircle(null);
      return;
    }
    setSelectedCircle((prev) => {
      if (!prev?.id || String(prev.id) !== String(latest.id)) return prev;
      return latest;
    });
  }, [circles, isEditing, selectedCircle?.id]);

  // Reprise d'une réunion (lien ?meeting= ou retour depuis une autre page) : une seule fois, puis on oublie
  // la demande ; si la réunion n'est plus ouverte, la page reprend son comportement normal.
  const forgetResumeRequest = useCallback(() => {
    navigate('/holacracy', { replace: true, state: null });
  }, [navigate]);

  useEffect(() => {
    if (!pendingResumeMeetingId) return undefined;
    const timer = window.setTimeout(forgetResumeRequest, 6000);
    return () => window.clearTimeout(timer);
  }, [pendingResumeMeetingId, forgetResumeRequest]);

  useEffect(() => {
    if (!pendingResumeMeetingId) return;
    if (!Array.isArray(openMeetings) || openMeetings.length === 0) return;
    const meeting = openMeetings.find((item) => String(item?.id) === String(pendingResumeMeetingId));
    if (!meeting) return;
    if (String(activeMeeting?.id) === String(meeting.id) && activeTab === 'meeting') {
      forgetResumeRequest();
      return;
    }
    const targetCircle = circles.find((circle) => String(circle?.id) === String(meeting?.circle_id));
    if (!targetCircle) return;
    handleJoinMeeting(meeting);
    forgetResumeRequest();
  }, [pendingResumeMeetingId, openMeetings, circles, activeMeeting?.id, activeTab, handleJoinMeeting, forgetResumeRequest]);

  const handleCircleClick = useCallback((circle) => {
    // Allow selecting virtual root (no id) to handle consolidation
    const latest = circle?.id
      ? circles.find((item) => String(item?.id) === String(circle.id))
      : null;
    setSelectedCircle(latest || normalizeCircle(circle));
    setIsEditing(false);
    setNewCircleParentId(null);
    setSelectedEmployeeId(null);
  }, [circles]);

  const handleSelectEmployee = useCallback((employeeId) => {
    if (employeeId === null || employeeId === undefined || employeeId === '') return;
    const normalized = Number(employeeId);
    const nextId = Number.isNaN(normalized) ? employeeId : normalized;
    setSelectedEmployeeId((prev) => (String(prev) === String(nextId) ? null : nextId));
    setIsEditing(false);
  }, []);

  const handleSaveRoleFocus = useCallback(
    async (circleId, employeeId, focusText) => {
      const trimmed = typeof focusText === 'string' ? focusText.trim() : '';
      const key = buildFocusKey(circleId, employeeId);
      setRoleFocusMap((prev) => {
        const next = { ...prev };
        if (trimmed) {
          next[key] = focusText;
        } else {
          delete next[key];
        }
        return next;
      });
      try {
        const response = await fetch(`${API_URL}/circle-focus`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({
            circle_id: circleId,
            employee_id: employeeId,
            focus_text: focusText,
          }),
        });
        if (!response.ok) throw new Error(await readError(response, "Le focus n'a pas pu être enregistré."));
      } catch (err) {
        setStructureError(err.message);
        fetchRoleFocus();
      }
    },
    [API_URL, getAuthHeaders, fetchRoleFocus]
  );

  useEffect(() => {
    fetchRoleFocus();
  }, [fetchRoleFocus]);

  // Modification directe d'un cercle : seuls les champs changés partent ; affichage immédiat, retour arrière
  // et message clair si le serveur refuse.
  const saveCirclePatch = useCallback(
    async (changes, circleId = selectedCircle?.id) => {
      const current = circles.find((circle) => String(circle.id) === String(circleId));
      if (!current) return false;
      const patch = circlePatch(current, changes);
      if (Object.keys(patch).length === 0) return true;
      const optimistic = normalizeCircle({ ...current, ...patch });
      setCircles((prev) => prev.map((circle) => (String(circle.id) === String(circleId) ? optimistic : circle)));
      setSelectedCircle((prev) => (prev && String(prev.id) === String(circleId) ? optimistic : prev));
      try {
        const response = await fetch(`${API_URL}/circles/${circleId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify(patch),
        });
        if (!response.ok) throw new Error(await readError(response, "La modification n'a pas pu être enregistrée."));
        const saved = normalizeCircle(await response.json());
        setCircles((prev) => prev.map((circle) => (String(circle.id) === String(circleId) ? saved : circle)));
        setSelectedCircle((prev) => (prev && String(prev.id) === String(circleId) ? saved : prev));
        setStructureError(null);
        return true;
      } catch (err) {
        setCircles((prev) => prev.map((circle) => (String(circle.id) === String(circleId) ? current : circle)));
        setSelectedCircle((prev) => (prev && String(prev.id) === String(circleId) ? current : prev));
        setStructureError(err.message);
        return false;
      }
    },
    [API_URL, circles, getAuthHeaders, selectedCircle?.id]
  );

  // Fiche en super édition : création (POST complet) ou modification (champs changés seulement).
  const handleSave = async (formData) => {
    if (selectedCircle?.id) {
      const ok = await saveCirclePatch(formData, selectedCircle.id);
      if (ok) setIsEditing(false);
      return ok;
    }
    try {
      const response = await fetch(`${API_URL}/circles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ ...formData, parent_id: formData.parent_id ?? newCircleParentId ?? null }),
      });
      if (!response.ok) throw new Error(await readError(response, "La création n'a pas pu être enregistrée."));
      const saved = normalizeCircle(await response.json());
      await fetchCircles();
      setSelectedCircle(saved);
      setIsEditing(false);
      setNewCircleParentId(null);
      setStructureError(null);
      return true;
    } catch (err) {
      setStructureError(err.message);
      return false;
    }
  };

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    if (!selectedCircle && newCircleParentId) {
      const parentCircle = circles.find((circle) => String(circle.id) === String(newCircleParentId)) || null;
      setSelectedCircle(parentCircle);
    }
    setNewCircleParentId(null);
  }, [selectedCircle, newCircleParentId, circles]);

  const handleDelete = async (id) => {
    const target = circles.find((circle) => String(circle.id) === String(id));
    const parent = target?.parent_id ? circles.find((circle) => String(circle.id) === String(target.parent_id)) : null;
    const children = childrenByParent[id]?.length || 0;
    const detail = children > 0
      ? `\nSes ${children} rôle(s) et sous-cercle(s) remonteront dans « ${parent?.name || 'le niveau supérieur'} ».`
      : '';
    if (!window.confirm(`Supprimer « ${target?.name || 'ce cercle'} » ?${detail}`)) return;
    try {
      const response = await fetch(`${API_URL}/circles/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error(await readError(response, 'La suppression a échoué.'));
      await fetchCircles();
      setSelectedCircle(parent || null);
      setIsEditing(false);
      setStructureError(null);
    } catch (err) {
      setStructureError(err.message);
    }
  };

  const handleInitializeRoot = async () => {
    try {
      const currentUserId = effectiveUserId ? Number(effectiveUserId) : null;
      const ownerIds = currentUserId !== null && !Number.isNaN(currentUserId) ? [currentUserId] : [];
      const rootCircle = {
        name: 'Ancrage',
        purpose: "Raison d'être de l'organisation",
        domains: [],
        accountabilities: [],
        strategy: [],
        owner_id: ownerIds[0] ?? null,
        owner_ids: ownerIds,
        parent_id: null,
      };

      const response = await fetch(`${API_URL}/circles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(rootCircle),
      });

      if (!response.ok) throw new Error(await readError(response, "Le cercle d'ancrage n'a pas pu être créé."));

      const saved = await response.json();
      await fetchCircles();
      setSelectedCircle(normalizeCircle(saved));
    } catch (err) {
      setStructureError(err.message);
    }
  };

  const handleConsolidateRoots = async () => {
    if (!selectedCircle || selectedCircle.id) return;

    try {
      // 1. Create the new real root
      const rootCircle = {
        name: 'Organisation',
        purpose: 'Regroupe les cercles de premier niveau',
        domains: [],
        accountabilities: [],
        strategy: [],
        owner_id: null,
        owner_ids: [],
        parent_id: null,
      };

      const createResponse = await fetch(`${API_URL}/circles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(rootCircle),
      });

      if (!createResponse.ok) throw new Error(await readError(createResponse, "Le cercle d'ancrage n'a pas pu être créé."));
      const newRoot = await createResponse.json();

      // 2. Les anciennes racines passent sous la nouvelle (seul le parent change).
      const children = (selectedCircle.children || []).filter((child) => child.id);
      const failed = [];
      await Promise.all(children.map(async (child) => {
        const updateResponse = await fetch(`${API_URL}/circles/${child.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({ parent_id: newRoot.id }),
        });
        if (!updateResponse.ok) failed.push(child.name);
      }));

      await fetchCircles();
      setSelectedCircle(normalizeCircle(newRoot));
      if (failed.length > 0) setStructureError(`Non déplacés : ${failed.join(', ')}.`);
    } catch (err) {
      setStructureError(err.message);
    }
  };

  const handleAddSubCircle = () => {
    if (selectedCircle) {
      setNewCircleParentId(selectedCircle.id);
      setSelectedCircle(null);
      setIsEditing(true);
    }
  };

  const handleToggleSuperEdit = useCallback(() => {
    if (!isSuperEditor) return;
    setSuperEditMode((prev) => {
      const next = !prev;
      if (!next) {
        setIsEditing(false);
      }
      return next;
    });
  }, [isSuperEditor]);

  const handleStartSuperEdit = useCallback(() => {
    if (!selectedCircle?.id) return;
    setNewCircleParentId(null);
    setIsEditing(true);
  }, [selectedCircle]);

  // --- Gestion des tensions ---
  const buildSnapshotFromCircle = (circle) => ({
    name: circle?.name || '',
    purpose: circle?.purpose || '',
    domains: circle?.domains || [],
    accountabilities: circle?.accountabilities || [],
    reserved_domains: circle?.reserved_domains || [],
    parent_id: circle?.parent_id || null,
  });

  const buildTensionTitleChanges = (draft) => {
    const ops = draft?.operations || [];
    const circlesById = new Map((circles || []).map((c) => [String(c.id), c]));
    const opLines = ops.length > 0
      ? ops.map((op) => {
        const actionLabel = op.action === 'create'
          ? 'Création de rôle'
          : op.action === 'delete'
            ? 'Suppression de rôle'
            : 'Modification de rôle';
        const roleName =
          op.data?.name ||
          op.original_data?.name ||
          circlesById.get(String(op.target_circle_id || op.circle_id))?.name ||
          'role';
        const changes = [];
        if (op.action === 'edit' && op.original_data && op.data) {
          if (op.data.name !== undefined && op.data.name !== op.original_data.name) changes.push('nom');
          if (op.data.purpose !== undefined && op.data.purpose !== op.original_data.purpose) changes.push('raison');
          if (Array.isArray(op.data.domains) && JSON.stringify(op.data.domains) !== JSON.stringify(op.original_data.domains || [])) {
            changes.push('domaines');
          }
          if (Array.isArray(op.data.accountabilities) && JSON.stringify(op.data.accountabilities) !== JSON.stringify(op.original_data.accountabilities || [])) {
            changes.push('redevabilités');
          }
          if (Array.isArray(op.data.reserved_domains) && JSON.stringify(op.data.reserved_domains) !== JSON.stringify(op.original_data.reserved_domains || [])) {
            changes.push('domaines à effet');
          }
        }
        if (op.action === 'create') {
          const parentName = circlesById.get(String(op.data?.parent_id || op.circle_id))?.name;
          if (parentName) changes.push(`parent ${parentName}`);
        }
        const changeLabel = changes.length > 0 ? ` (${changes.join(', ')})` : '';
        return `- ${actionLabel}: ${roleName}${changeLabel}`;
      }).join('\n')
      : `- Modification role: ${draft?.original_data?.name || 'role'}`;

    return opLines;
  };

  const normalizeTensionTitle = (raw) => {
    if (!raw) return '';
    const cleaned = String(raw)
      .replace(/[\r\n]+/g, ' ')
      .replace(/^"|"$/g, '')
      .replace(/^'|'$/g, '')
      .trim();
    if (!cleaned) return '';
    const words = cleaned.split(/\s+/);
    return words.slice(0, 5).join(' ');
  };

  const generateTensionTitle = async (draft) => {
    const changes = buildTensionTitleChanges(draft);
    const data = await apiService.suggestCircleTensionTitle(changes);
    return normalizeTensionTitle(data?.suggested_title);
  };

  const handleStartTension = (circleToUse = selectedCircle, action = 'edit') => {
    let original = {};
    let proposed = {};
    let operations = [];

    let tensionCircleId = circleToUse?.id || null;
    if (circleToUse?.id) {
      const hasChildren = (childrenByParent[circleToUse.id] || []).length > 0;
      const isRoleTarget = Boolean(circleToUse.parent_id) && !hasChildren;
      if (isRoleTarget) {
        // Un rôle n'a pas de gouvernance propre : la tension va au cercle qui le contient.
        tensionCircleId = circleToUse.parent_id;
        if (action === 'create') {
          setStructureError('Un rôle ne contient pas de rôle : propose la création depuis son cercle.');
          return;
        }
      }
    }

    if (action === 'create') {
      original = {};
      proposed = {
        name: '',
        purpose: '',
        domains: [],
        accountabilities: [],
        parent_id: circleToUse?.id || null,
      };
      operations = [
        {
          action: 'create',
          circle_id: circleToUse?.id || null,
          target_circle_id: null,
          data: proposed,
          original_data: original,
        },
      ];
    } else {
      if (!circleToUse?.id) {
        setStructureError('Choisis un cercle ou un rôle sur la carte pour proposer un changement.');
        return;
      }
      original = buildSnapshotFromCircle(circleToUse);
      proposed = { ...original };
      operations = [
        {
          action: action || 'edit',
          circle_id: tensionCircleId,
          target_circle_id: circleToUse.id,
          data: proposed,
          original_data: original,
        },
      ];
    }

    setTensionDraft({
      id: null,
      circle_id: tensionCircleId,
      title: '',
      summary: '',
      status: 'draft',
      expressed_by_role_id: null,
      action,
      original_data: original,
      proposed_data: proposed,
      operations,
    });
    setSelectedTensionId(null);
    setShowTensionModal(true);
  };

  const handleSelectTension = (tension) => {
    setSelectedTensionId(tension.id);
    setTensionDraft(normalizeTension(tension));
    setActiveTab('tensions');
    setShowTensionModal(false);
    if ((tension.status === 'proposal' || tension.status === 'applied') && tension.id) {
      fetchTensionVotes(tension.id);
    } else {
      setTensionVotes([]);
    }
  };

  const handleCancelTension = () => {
    setTensionDraft(null);
    setSelectedTensionId(null);
    setShowTensionModal(false);
  };

  const updateTensionField = (key, value) => {
    setTensionDraft((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const updateTensionProposedField = (key, value) => {
    setTensionDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        proposed_data: {
          ...(prev.proposed_data || {}),
          [key]: value,
        },
      };
    });
  };

  const updateTensionList = (listName, updater) => {
    setTensionDraft((prev) => {
      if (!prev) return prev;
      const currentList = Array.isArray(prev.proposed_data?.[listName]) ? prev.proposed_data[listName] : [];
      const nextList = updater(currentList);
      return {
        ...prev,
        proposed_data: {
          ...(prev.proposed_data || {}),
          [listName]: nextList,
        },
      };
    });
  };

  const isTensionFieldChanged = (field) => {
    if (!tensionDraft) return false;
    const original = tensionDraft.original_data || {};
    const proposed = tensionDraft.proposed_data || {};
    if (Array.isArray(original[field]) || Array.isArray(proposed[field])) {
      return JSON.stringify(original[field] || []) !== JSON.stringify(proposed[field] || []);
    }
    return (proposed[field] || '') !== (original[field] || '');
  };

  const handleTensionActionChange = (nextAction) => {
    setTensionDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        action: nextAction,
        proposed_data: {
          ...(prev.proposed_data || {}),
          delete: nextAction === 'delete' ? true : undefined,
        },
      };
    });
  };

  const handleSaveTension = async () => {
    if (!tensionDraft) return;
    const userTitle = tensionDraft.title?.trim();
    const fallbackName =
      circles.find((c) => String(c.id) === String(tensionDraft.circle_id))?.name ||
      tensionDraft.original_data?.name ||
      'role';
    const fallbackTitle = userTitle || `Tension ${fallbackName}`;
    const shouldAutoTitle = !userTitle;
    if (!tensionDraft.operations || tensionDraft.operations.length === 0) {
      setTensionError('Ajoute au moins un changement (créer, modifier ou supprimer un rôle).');
      return;
    }
    if (shouldAutoTitle) {
      setTensionDraft((prev) => (prev ? { ...prev, title: fallbackTitle } : prev));
      setShowTensionModal(false);
    }
    try {
      setTensionsLoading(true);
      const operations = tensionDraft.operations || [];
      const firstOp = operations[0];
      const isCreateAction = firstOp?.action === 'create' || tensionDraft.action === 'create';
      const targetId = firstOp?.target_circle_id ?? firstOp?.circle_id ?? tensionDraft.circle_id ?? null;
      const rawParentId = firstOp?.data?.parent_id !== undefined
        ? firstOp.data.parent_id
        : isCreateAction
          ? (firstOp?.circle_id ?? tensionDraft.circle_id ?? null)
          : (tensionDraft.proposed_data?.parent_id ?? null);
      const safeParentId = !isCreateAction && rawParentId !== null && rawParentId !== undefined && targetId !== null && String(rawParentId) === String(targetId)
        ? null
        : rawParentId;
      const proposed_data =
        firstOp?.action === 'delete' || tensionDraft.action === 'delete'
          ? { delete: true }
          : firstOp?.data
            ? {
              name: firstOp.data.name || '',
              purpose: firstOp.data.purpose || '',
              domains: firstOp.data.domains || [],
              accountabilities: firstOp.data.accountabilities || [],
              reserved_domains: firstOp.data.reserved_domains || [],
              parent_id: safeParentId,
            }
            : {
              name: tensionDraft.proposed_data?.name || '',
              purpose: tensionDraft.proposed_data?.purpose || '',
              domains: tensionDraft.proposed_data?.domains || [],
              accountabilities: tensionDraft.proposed_data?.accountabilities || [],
              parent_id: safeParentId,
            };

      const payload = {
        circle_id: tensionDraft.circle_id,
        expressed_by_role_id: tensionDraft.expressed_by_role_id ? Number(tensionDraft.expressed_by_role_id) : null,
        action: firstOp?.action || tensionDraft.action || 'edit',
        title: fallbackTitle,
        summary: tensionDraft.summary,
        proposed_data,
        operations,
      };
      const isUpdate = Boolean(tensionDraft.id);
      const url = isUpdate
        ? `${API_URL}/circle-tensions/${tensionDraft.id}`
        : `${API_URL}/circle-tensions`;
      const method = isUpdate ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(await readError(response, "Impossible d'enregistrer la tension."));
      const saved = normalizeTension(await response.json());
      setTensionDraft(saved);
      setSelectedTensionId(saved.id);
      await fetchTensions();
      if (!shouldAutoTitle) {
        setShowTensionModal(false);
      }
      if (shouldAutoTitle) {
        generateTensionTitle(tensionDraft)
          .then(async (suggested) => {
            const nextTitle = normalizeTensionTitle(suggested);
            if (!nextTitle || nextTitle === fallbackTitle) return;
            const titleResponse = await fetch(
              `${API_URL}/circle-tensions/${saved.id}`,
              {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                body: JSON.stringify({ title: nextTitle }),
              },
            );
            if (!titleResponse.ok) return;
            const updated = normalizeTension(await titleResponse.json());
            setTensionDraft(updated);
            await fetchTensions();
          })
          .catch((err) => {
            console.warn('Failed to update tension title:', err);
          });
      }
    } catch (err) {
      setTensionError(err.message);
    } finally {
      setTensionsLoading(false);
    }
  };

  const handleDeleteTension = async (tensionId) => {
    if (!window.confirm("Supprimer cette tension ?")) return;
    try {
      setTensionsLoading(true);
      const response = await fetch(`${API_URL}/circle-tensions/${tensionId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error(await readError(response, 'Suppression impossible.'));
      setTensionDraft(null);
      setSelectedTensionId(null);
      await fetchTensions();
    } catch (err) {
      setTensionError(err.message);
    } finally {
      setTensionsLoading(false);
    }
  };

  const handleProposeTension = async (tensionId) => {
    try {
      setTensionsLoading(true);
      const response = await fetch(`${API_URL}/circle-votes/propose/${tensionId}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error(await readError(response, 'Impossible de lancer le vote.'));
      await fetchTensions();
      await fetchVotes();
      await fetchTensionVotes(tensionId);
    } catch (err) {
      setTensionError(err.message);
    } finally {
      setTensionsLoading(false);
    }
  };

  const handleVoteDecision = async (voteId, decision) => {
    try {
      setVotesLoading(true);
      const response = await fetch(`${API_URL}/circle-votes/${voteId}/decision?decision=${decision}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error(await readError(response, 'Vote impossible.'));
      await fetchVotes();
      await fetchTensions();
      await fetchCircles();
      if (tensionDraft?.id) {
        await fetchTensionVotes(tensionDraft.id);
      }
    } catch (err) {
      setVotesError(err.message);
    } finally {
      setVotesLoading(false);
    }
  };

  const handleWithdrawProposal = async (tensionId) => {
    try {
      setTensionsLoading(true);
      const response = await fetch(`${API_URL}/circle-votes/propose/${tensionId}/withdraw`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error(await readError(response, 'Impossible de retirer la proposition.'));
      await fetchTensions();
      await fetchVotes();
      await fetchTensionVotes(tensionId);
    } catch (err) {
      setTensionError(err.message);
    } finally {
      setTensionsLoading(false);
    }
  };


  // Permission Logic
  const userLeadsCircle = useCallback(
    (circle) => {
      if (!effectiveUserId || !circle) return false;
      const userId = Number(effectiveUserId);
      if (Number.isNaN(userId)) return false;
      const owners = [];
      if (circle.owner_id) owners.push(circle.owner_id);
      if (Array.isArray(circle.owner_ids)) owners.push(...circle.owner_ids);
      return owners.some((id) => Number(id) === userId);
    },
    [effectiveUserId]
  );

  const isRoleCircle = useCallback(
    (circle) => {
      if (!circle?.id) return false;
      if (!circle.parent_id) return false;
      return !(childrenByParent[circle.id]?.length);
    },
    [childrenByParent]
  );

  const isLeafRole = React.useMemo(() => {
    if (!selectedCircle?.id) return false;
    if (!selectedCircle.parent_id) return false;
    return !(childrenByParent[selectedCircle.id]?.length);
  }, [selectedCircle, childrenByParent]);


  const directRoles = React.useMemo(() => {
    if (!selectedCircle?.id) return [];
    return (childrenByParent[selectedCircle.id] || []).filter(isRoleCircle);
  }, [selectedCircle, childrenByParent, isRoleCircle]);

  const getDescendantIds = useCallback(
    (circleId) => {
      const ids = new Set();
      const stack = [circleId];
      while (stack.length > 0) {
        const current = stack.pop();
        if (!current || ids.has(current)) continue;
        ids.add(current);
        const children = childrenByParent[current] || [];
        children.forEach((child) => {
          if (child?.id) stack.push(child.id);
        });
      }
      return ids;
    },
    [childrenByParent]
  );

  const canViewSelectedCircleProjects = Boolean(selectedCircle?.id);
  const isInsightsTab = activeTab === 'insights';

  const projectsInScope = React.useMemo(() => {
    if (!selectedCircle?.id) return [];
    const ids = getDescendantIds(selectedCircle.id);
    const list = (projects || []).filter((project) => {
      if (!project?.circle_id) return false;
      return ids.has(Number(project.circle_id));
    });
    return list.sort((a, b) => {
      const left = Number(a.project_number || a.id || 0);
      const right = Number(b.project_number || b.id || 0);
      return left - right;
    });
  }, [selectedCircle, projects, getDescendantIds]);

  useEffect(() => {
    const shouldLoadScopeMetrics = activeTab === 'insights' || activeTab === 'structure';
    if (!shouldLoadScopeMetrics) return;
    if (!projectsInScope.length) return;
    let cancelled = false;
    let idleId = null;
    let timeoutId = null;
    const pendingKeys = new Set();
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    const loadMetrics = async () => {
      const pending = projectsInScope.filter((project) => {
        const key = String(project.id);
        return (
          !projectMetricsByIdRef.current[key] &&
          !projectMetricsLoadingRef.current[key]
        );
      });
      if (pending.length === 0) return;
      setProjectMetricsError(null);
      const loadingPatch = {};
      pending.forEach((project) => {
        const key = String(project.id);
        pendingKeys.add(key);
        loadingPatch[key] = true;
      });
      if (isMountedRef.current && !cancelled) {
        setProjectMetricsLoading((prev) => ({ ...prev, ...loadingPatch }));
      }
      const concurrency = lowPowerDevice ? 2 : 4;
      let cursor = 0;
      const loadedPatch = {};
      let loadError = null;
      const runProject = async (project, attempt = 0) => {
        const key = String(project.id);
        try {
          const data = await fetchProjectMetrics(project.id);
          if (!isMountedRef.current || cancelled) return;
          loadedPatch[key] = data;
        } catch (err) {
          if (!isMountedRef.current || cancelled) return;
          if (attempt < 2) {
            await sleep(300 * (attempt + 1));
            return runProject(project, attempt + 1);
          }
          if (!loadError) {
            loadError = err?.message || 'Erreur lors du chargement des metrics projet.';
          }
        }
      };
      const workers = Array.from(
        { length: Math.min(concurrency, pending.length) },
        async () => {
          while (!cancelled) {
            const project = pending[cursor];
            cursor += 1;
            if (!project) break;
            await runProject(project);
          }
        },
      );
      await Promise.all(workers);
      if (!isMountedRef.current || cancelled) return;
      if (Object.keys(loadedPatch).length > 0) {
        setProjectMetricsById((prev) => ({ ...prev, ...loadedPatch }));
      }
      if (loadError) {
        setProjectMetricsError(loadError);
      }
      setProjectMetricsLoading((prev) => {
        const next = { ...prev };
        pendingKeys.forEach((key) => {
          next[key] = false;
        });
        return next;
      });
    };
    if (activeTab === 'structure') {
      if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
        idleId = window.requestIdleCallback(() => {
          loadMetrics();
        }, { timeout: 1400 });
      } else {
        timeoutId = window.setTimeout(() => {
          loadMetrics();
        }, 250);
      }
    } else {
      loadMetrics();
    }
    return () => {
      cancelled = true;
      if (idleId !== null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      if (!isMountedRef.current || pendingKeys.size === 0) return;
      setProjectMetricsLoading((prev) => {
        const next = { ...prev };
        pendingKeys.forEach((key) => {
          next[key] = false;
        });
        return next;
      });
    };
  }, [activeTab, projectsInScope, fetchProjectMetrics, lowPowerDevice]);

  const metricsRangeInfo = React.useMemo(() => {
    const start = parseDateInput(metricsRange.start);
    const end = parseDateInput(metricsRange.end);
    if (!start || !end) {
      return { start: null, end: null, isValid: false };
    }
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    return { start, end, isValid: start <= end };
  }, [metricsRange, parseDateInput]);

  const projectRangeStatsById = React.useMemo(() => {
    const statsById = {};
    if (!metricsRangeInfo.isValid) return statsById;
    projectsInScope.forEach((project) => {
      const key = String(project.id);
      const metrics = projectMetricsById[key];
      if (!Array.isArray(metrics) || metrics.length === 0) return;
      let hours = 0;
      let expenses = 0;
      let tokens = 0;
      let hasEntry = false;
      metrics.forEach((entry) => {
        const entryDate = parseDateInput(entry.date);
        if (!entryDate) return;
        if (entryDate >= metricsRangeInfo.start && entryDate <= metricsRangeInfo.end) {
          hours += Number(entry.hours_daily || 0);
          expenses += Number(entry.expenses_daily || 0);
          tokens += Number(entry.tokens_daily || 0);
          hasEntry = true;
        }
      });
      statsById[key] = { hours, expenses, tokens, hasEntry };
    });
    return statsById;
  }, [projectsInScope, projectMetricsById, metricsRangeInfo, parseDateInput]);

  const circleMetricsSummary = React.useMemo(() => {
    if (!metricsRangeInfo.isValid) {
      return { hours: 0, expenses: 0, projects: 0, covered: 0, velocity: 0 };
    }
    let hours = 0;
    let expenses = 0;
    let covered = 0;
    let velocitySum = 0;
    let velocityCount = 0;
    projectsInScope.forEach((project) => {
      const stats = projectRangeStatsById[String(project.id)];
      if (!stats || !stats.hasEntry) return;
      covered += 1;
      hours += Number(stats.hours || 0);
      expenses += Number(stats.expenses || 0);
      if (stats.hours > 0) {
        velocitySum += (Number(stats.tokens || 0) * DAILY_HOURS) / Number(stats.hours);
        velocityCount += 1;
      }
    });
    const velocity = velocityCount > 0 ? velocitySum / velocityCount : 0;
    return {
      hours,
      expenses,
      projects: projectsInScope.length,
      covered,
      velocity,
    };
  }, [projectsInScope, projectRangeStatsById, metricsRangeInfo, DAILY_HOURS]);

  const circleMetricsLoading = React.useMemo(() => {
    if (!projectsInScope.length) return false;
    return projectsInScope.some(
      (project) => projectMetricsLoading[String(project.id)],
    );
  }, [projectsInScope, projectMetricsLoading]);

  const projectTotals = React.useMemo(() => {
    if (!isInsightsTab) return [];
    return (projectsInScope || []).map((project) => {
      const stats = projectRangeStatsById[String(project.id)] || {
        hours: 0,
        expenses: 0,
      };
      return {
        project,
        hours: Number(stats.hours || 0),
        expenses: Number(stats.expenses || 0),
      };
    });
  }, [projectsInScope, projectRangeStatsById, isInsightsTab]);

  const projectTotalsById = React.useMemo(() => {
    if (!isInsightsTab) return {};
    const map = {};
    projectTotals.forEach((item) => {
      map[String(item.project.id)] = item;
    });
    return map;
  }, [projectTotals, isInsightsTab]);

  const projectsInScopeByCircle = React.useMemo(() => {
    const map = {};
    projectsInScope.forEach((project) => {
      const key = String(project.circle_id || '');
      if (!map[key]) map[key] = [];
      map[key].push(project);
    });
    return map;
  }, [projectsInScope]);

  const minVisualValue = 0.05;
  const getProjectMetricValue = useCallback(
    (project, metricKey) => {
      const totals = projectTotalsById[String(project.id)] || { hours: 0, expenses: 0 };
      const rawValue = Number(totals[metricKey] || 0);
      if (!Number.isFinite(rawValue)) return minVisualValue;
      return rawValue > 0 ? rawValue : minVisualValue;
    },
    [projectTotalsById]
  );

  const buildInsightHierarchy = useCallback(
    (rootCircle, metricKey) => {
      if (!rootCircle?.id) return null;
      const buildNode = (circle) => {
        const childCircles = (childrenByParent[circle.id] || []).map(buildNode);
        const projectNodes = (projectsInScopeByCircle[String(circle.id)] || [])
          .map((project) => {
            const totals = projectTotalsById[String(project.id)] || { hours: 0, expenses: 0 };
            const rawValue = Number(totals[metricKey] || 0);
            const sizeValue = getProjectMetricValue(project, metricKey);
            return {
              id: `project-${project.id}`,
              name: project.name || `Projet ${project.id}`,
              nodeType: 'project',
              project_id: project.id,
              project_number: project.project_number,
              circle_id: project.circle_id,
              value: sizeValue,
              rawValue,
            };
          });
        const children = [...childCircles, ...projectNodes];
        const rawValue = children.reduce((sum, child) => sum + Number(child.rawValue || 0), 0);
        const baseNode = {
          id: circle.id,
          name: circle.name || `Cercle ${circle.id}`,
          parent_id: circle.parent_id ?? null,
          owner_id: circle.owner_id,
          owner_ids: circle.owner_ids,
          nodeType: 'circle',
        };
        if (children.length > 0) {
          return { ...baseNode, children, rawValue };
        }
        return { ...baseNode, value: minVisualValue, rawValue: 0 };
      };
      return buildNode(rootCircle);
    },
    [childrenByParent, projectsInScopeByCircle, getProjectMetricValue, projectTotalsById]
  );

  const hoursHierarchy = React.useMemo(() => {
    if (!isInsightsTab) return null;
    return buildInsightHierarchy(selectedCircle, 'hours');
  }, [isInsightsTab, buildInsightHierarchy, selectedCircle]);
  const expensesHierarchy = React.useMemo(() => {
    if (!isInsightsTab) return null;
    return buildInsightHierarchy(selectedCircle, 'expenses');
  }, [isInsightsTab, buildInsightHierarchy, selectedCircle]);

  const isUsingSlotRange = React.useMemo(() => {
    if (!currentSlotRange) return false;
    return (
      metricsRange.start === currentSlotRange.start &&
      metricsRange.end === currentSlotRange.end
    );
  }, [metricsRange, currentSlotRange]);

  const mentionItems = React.useMemo(() => {
    const items = [];
    (circles || []).forEach((circle) => {
      if (!circle?.id || !circle?.name) return;
      const type = isRoleCircle(circle) ? 'role' : 'circle';
      items.push({ type, id: circle.id, name: circle.name });
    });
    (projects || []).forEach((project) => {
      if (!project?.id || !project?.name) return;
      items.push({ type: 'project', id: project.id, name: project.name });
    });
    orgRules.forEach((rule) => items.push({ type: 'rule', id: rule.id, name: rule.name }));
    return items;
  }, [circles, projects, isRoleCircle, orgRules]);

  const openRule = useCallback((ruleId) => {
    setFocusRuleId(ruleId);
    setActiveTab('rules');
  }, []);
  const orgMentions = React.useMemo(() => ({
    mentionItems,
    ruleById: new Map(orgRules.map((rule) => [Number(rule.id), rule])),
    onOpenRule: openRule,
  }), [mentionItems, orgRules, openRule]);

  const findEmployee = useCallback(
    (id) => employees.find((e) => Number(e.id) === Number(id)),
    [employees]
  );

  const activeEmployees = React.useMemo(
    () => employees.filter((emp) => emp?.is_active !== false),
    [employees]
  );


  const canEditLeader = React.useMemo(() => {
    if (superEditMode) return true;
    if (!user || !selectedCircle || !selectedCircle.id) return false;
    const parent = circles.find((c) => String(c.id) === String(selectedCircle.parent_id));
    return userLeadsCircle(parent);
  }, [superEditMode, user, selectedCircle, circles, userLeadsCircle]);

  const canEditStrategy = React.useMemo(
    () => superEditMode || userLeadsCircle(selectedCircle),
    [superEditMode, userLeadsCircle, selectedCircle]
  );

  const canEditVoteTimeout = React.useMemo(
    () => superEditMode || userLeadsCircle(selectedCircle),
    [superEditMode, userLeadsCircle, selectedCircle]
  );

  const canEditColor = React.useMemo(
    () => superEditMode || userLeadsCircle(selectedCircle),
    [superEditMode, userLeadsCircle, selectedCircle]
  );

  const canAddSubCircle = React.useMemo(() => {
    if (!selectedCircle || !selectedCircle.id) return false;
    return superEditMode || userLeadsCircle(selectedCircle);
  }, [selectedCircle, userLeadsCircle, superEditMode]);

  const canEditRoleFocus = React.useMemo(
    () => {
      if (superEditMode) return true;
      if (!selectedCircle?.id) return false;
      if (isLeafRole) {
        const parent = circles.find((circle) => String(circle.id) === String(selectedCircle.parent_id));
        return userLeadsCircle(parent);
      }
      return userLeadsCircle(selectedCircle);
    },
    [superEditMode, userLeadsCircle, selectedCircle, circles, isLeafRole]
  );

  if (loading) return <div className="p-8 text-center">Chargement...</div>;
  if (error) return <div className="p-8 text-center text-red-500">Erreur: {error}</div>;

  const isMeeting = activeTab === 'meeting';
  // Erreurs de la fiche, et échec d'ouverture de réunion (sinon invisible tant qu'on n'est pas dans l'onglet).
  const visibleMeetingError = !isMeeting && meetingError && meetingError !== dismissedMeetingError ? meetingError : null;
  const structureBanner = structureError || visibleMeetingError;
  const dismissStructureBanner = () => {
    setStructureError(null);
    if (meetingError) setDismissedMeetingError(meetingError);
  };
  const isWideTab = activeTab === 'tensions' || activeTab === 'insights';
  const isMeetingSecretary = String(activeMeeting?.created_by) === String(effectiveUserId);
  // La réunion suivie reste ouverte quand on parcourt la carte : son cercle, pas celui sélectionné.
  const meetingCircle = (activeMeeting && circles.find((c) => String(c.id) === String(activeMeeting.circle_id))) || selectedCircle;
  const pendingVotesCount = Array.isArray(votes) ? votes.length : 0;
  const pendingVotesLabel = pendingVotesCount > 9 ? '9+' : String(pendingVotesCount);
  const effectiveMeetingProjectId = isMeetingSecretary
    ? selectedMeetingProjectId
    : (selectedMeetingProjectIdShared ?? selectedMeetingProjectId);
  const meetingHistoryModal = (
    <MeetingHistoryModal
      isOpen={showMeetingHistory}
      onClose={handleCloseMeetingHistory}
      meetingHistory={meetingHistory}
      meetingHistoryLoading={meetingHistoryLoading}
      meetingHistoryError={meetingHistoryError}
      circleName={selectedCircle?.name}
    />
  );
  const TABS = [
    { key: 'structure', label: 'Structure' },
    { key: 'insights', label: 'Chiffres' },
    { key: 'rules', label: 'Règles', title: 'Les règles de l’organisation, citées dans les redevabilités et les domaines' },
    { key: 'tensions', label: 'Tensions' },
    { key: 'points', label: 'Points', title: 'Points à aborder à la prochaine réunion tactique' },
    { key: 'votes', label: 'Votes', badge: pendingVotesCount > 0 ? pendingVotesLabel : null },
    ...(activeMeeting ? [{ key: 'meeting', label: 'Réunion', live: true }] : []),
  ];
  const renderTabs = () => (
    <div className="flex items-center gap-2 mb-3 min-w-0">
      <div className="flex min-w-0 items-center gap-0.5 overflow-x-auto rounded-xl bg-slate-200/70 p-1">
        {TABS.map(({ key, label, badge, live, title }) => (
          <button
            key={key}
            type="button"
            onClick={() => setActiveTab(key)}
            title={title}
            className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
              activeTab === key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            {live && <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />}
            {label}
            {badge && (
              <span className="inline-flex items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-semibold text-white">
                {badge}
              </span>
            )}
          </button>
        ))}
      </div>
      {activeTab === 'tensions' && (
        <button
          type="button"
          onClick={fetchTensions}
          className="ml-auto p-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600"
          title="Actualiser mes tensions"
        >
          <RefreshCcw size={14} />
        </button>
      )}
    </div>
  );

  const renderStructureCard = (compact = false) => {
    const emptyState = (
      <div className="flex flex-col items-center justify-center h-full text-slate-400">
        <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-4">
          <Plus size={28} className="text-slate-300" />
        </div>
        <p className="mb-4 text-sm font-medium text-center">Aucune structure définie</p>
        <button
          onClick={handleInitializeRoot}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg shadow-md hover:bg-indigo-700 transition-colors text-sm"
        >
          Initialiser le cercle d'Ancrage
        </button>
      </div>
    );
    if (!hierarchyData) {
      return (
        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 h-full flex items-center justify-center text-slate-400">
          {circles.length === 0 ? emptyState : 'Aucune structure'}
        </div>
      );
    }
    const circleClick = compact && isMeeting ? () => { } : handleCircleClick;
    const sizeStyle = compact
      ? {
        background: 'radial-gradient(circle at 35% 35%, #eef2ff 0, #ffffff 70%)',
        width: 'clamp(260px, 22vw, 420px)',
        height: 'clamp(260px, 22vw, 420px)',
      }
      : {};
    return (
      <div
        className={`bg-gradient-to-br from-white to-slate-50 rounded-2xl shadow-xl border border-slate-100 ${compact ? 'flex-shrink-0' : 'h-full'
          } flex flex-col overflow-hidden relative`}
        style={sizeStyle}
      >
        <div className={`${compact ? 'flex-1 w-full h-full p-4 flex items-center justify-center' : 'flex-1 w-full h-full'}`}>
          {circles.length === 0 ? (
            emptyState
          ) : (
            <div
              className="w-full h-full flex items-center justify-center overflow-hidden"
              style={{
                transformOrigin: 'center',
                transform: compact ? 'scale(1)' : 'scale(1)',
                pointerEvents: compact && isMeeting ? 'none' : 'auto',
              }}
            >
              <div style={{ width: '96%', height: '96%' }}>
                <CirclePack
                  data={hierarchyData}
                  onCircleClick={circleClick}
                  openMeetingCircleIds={openMeetingCircleIds}
                  selectedCircleId={selectedCircle?.id}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (isMeeting) {
    return (
      <div className="min-h-[calc(100vh-64px)] h-auto overflow-y-auto bg-gray-100">
        <div className="h-auto flex flex-col px-6 pt-2 pb-6">
          {renderTabs()}
          <div className="flex-1 border border-slate-100 bg-white rounded-2xl shadow-sm p-4 overflow-visible">
            <MeetingPanel
              selectedCircle={meetingCircle}
              activeMeeting={activeMeeting}
              meetingError={meetingError}
              onDismissError={clearMeetingError}
              meetingParticipants={meetingParticipants}
              meetingDraft={meetingDraft}
              meetingTensions={meetingTensions}
              meetingPoints={meetingPoints}
              meetingPreparedPoints={meetingPreparedPoints}
              meetingPreparedPointsLoading={meetingPreparedPointsLoading}
              selectedMeetingPointId={isMeetingSecretary ? selectedMeetingPointId : (selectedMeetingPointIdShared ?? selectedMeetingPointId)}
              selectedMeetingProjectId={effectiveMeetingProjectId}
              meetingProjectReviews={meetingProjectReviews}
              meetingPointComposer={meetingPointComposer}
              selectedMeetingTensionId={isMeetingSecretary ? selectedMeetingTensionId : (selectedMeetingTensionIdShared ?? selectedMeetingTensionId)}
              meetingNotes={meetingNotes}
              meetingPhase={meetingPhase}
              presenceCount={meetingPresenceCount}
              connectedUsers={meetingConnectedUsers}
              realtimeConnected={meetingRealtimeConnected}
              meetingFormVisible={meetingFormVisible}
              tensions={tensions}
              circles={circles}
              employees={employees}
              projects={projects}
              projectSprints={projectSprints}
              onFetchProjectSprints={fetchProjectSprints}
              user={user}
              meetingLoading={meetingLoading}
              selectedCircleIsRole={isLeafRole}
              onCloseMeeting={handleCloseMeeting}
              onImportTension={handleImportMeetingTension}
              onMeetingDraftChange={handleMeetingDraftChange}
              onCreateMeetingTension={handleCreateMeetingTension}
              onRefreshMeetingTensions={fetchMeetingTensions}
              onSelectMeetingTension={handleSelectMeetingTension}
              onLiveEditTension={handleLiveMeetingTensionEdit}
              onMeetingDecision={handleMeetingDecision}
              onAddMeetingPoint={handleAddMeetingPoint}
              onImportMeetingPoint={handleImportMeetingPoint}
              onSelectMeetingPoint={handleSelectMeetingPoint}
              onPointDecision={handleMeetingPointDecision}
              onPointToTension={handleMeetingPointToTension}
              onPointNotesChange={handleMeetingPointNotesChange}
              onPointComposerUpdate={handleMeetingPointComposerUpdate}
              onCreatePointTask={handleCreateTacticalTask}
              onCreatePointObjective={handleCreateTacticalObjective}
              onSelectMeetingProject={handleSelectMeetingProject}
              onSelectMeetingPhase={handleSelectMeetingPhase}
              onFetchProjectReviews={fetchMeetingProjectReviews}
              onUpdateProjectReview={handleUpdateMeetingProjectReview}
              meetingLive={meetingLive}
              liveActions={liveActions}
              meetingFocus={meetingFocus}
              meetingFocusList={meetingFocusList}
              focusActions={focusActions}
              apiUrl={API_URL}
              onFetchProjectObjectives={fetchProjectObjectives}
              onNotesChange={handleMeetingNotesChange}
              onToggleForm={handleToggleMeetingForm}
              onTransferSecretary={handleTransferSecretary}
              onInviteParticipant={handleInviteMeetingParticipant}
              metricsRange={metricsRange}
              metricsRangeValid={metricsRangeInfo.isValid}
            />
          </div>
        </div>
        {showTensionModal && tensionDraft && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto relative">
              <button
                className="absolute top-3 right-3 rounded-full p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                onClick={handleCancelTension}
                aria-label="Fermer"
              >
                &#10005;
              </button>
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-700">Tension de gouvernance</p>
                  <span className="text-xs text-slate-500">{selectedCircle?.name || tensionDraft.original_data?.name || 'Cercle'}</span>
                </div>
                {tensionError && <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{tensionError}</p>}
                <TensionEditor
                  tensionDraft={tensionDraft}
                  circles={circles}
                  user={user}
                  tensionVotes={tensionVotes}
                  tensionVotesLoading={tensionVotesLoading}
                  tensionsLoading={tensionsLoading}
                  isFieldChanged={isTensionFieldChanged}
                  onUpdateField={updateTensionField}
                  onUpdateProposedField={updateTensionProposedField}
                  onUpdateList={updateTensionList}
                  onChangeAction={handleTensionActionChange}
                  onDelete={handleDeleteTension}
                  onPropose={handleProposeTension}
                  onWithdraw={handleWithdrawProposal}
                  onCancel={handleCancelTension}
                  onSave={handleSaveTension}
                  defaultCircleId={tensionDraft?.circle_id ?? selectedCircle?.id}
                />
              </div>
            </div>
          </div>
        )}
        {meetingHistoryModal}
      </div>
    );
  }

  return (
    <OrgMentionsContext.Provider value={orgMentions}>
    <div className="flex h-[calc(100vh-64px)] bg-gray-100 overflow-hidden">
      <div
        className={`${isWideTab ? 'hidden' : 'w-1/2 lg:w-3/5 xl:w-2/3'} transition-all duration-500 ease-in-out h-full p-4`}
      >
        {renderStructureCard(false)}
      </div>

      <div
        className={`${isWideTab ? 'w-full' : 'w-1/2 lg:w-2/5 xl:w-1/3'} h-full p-4 pl-0 transition-all duration-300 flex flex-col`}
      >
        {renderTabs()}

        {activeTab === 'structure' ? (
          <StructurePanel
            isEditing={isEditing}
            selectedCircle={selectedCircle}
            employees={employees}
            parentId={selectedCircle ? selectedCircle.parent_id : newCircleParentId}
            isRole={selectedCircle ? (!!selectedCircle.parent_id && !(childrenByParent[selectedCircle.id]?.length)) : Boolean(newCircleParentId)}
            onSavePatch={saveCirclePatch}
            error={structureBanner}
            onDismissError={dismissStructureBanner}
            canAddSubCircle={canAddSubCircle}
            circleMemberIds={circleMemberIds}
            canEditLeader={canEditLeader}
            canEditStrategy={canEditStrategy}
            canEditVoteTimeout={canEditVoteTimeout}
            canEditColor={canEditColor}
            isLeafRole={isLeafRole}
            activeEmployees={activeEmployees}
            findEmployee={findEmployee}
            onAddSubCircle={handleAddSubCircle}
            onStartTension={handleStartTension}
            onOpenMeeting={handleOpenMeeting}
            onJoinMeeting={handleJoinMeeting}
            onOpenMeetingHistory={handleOpenMeetingHistory}
            meetingLoading={meetingLoading}
            openMeetingsByCircle={openMeetingsByCircle}
            onSave={handleSave}
            onDelete={handleDelete}
            onCancelEdit={handleCancelEdit}
            isSuperEditor={isSuperEditor}
            superEditMode={superEditMode}
            onToggleSuperEdit={handleToggleSuperEdit}
            onStartSuperEdit={handleStartSuperEdit}
            onConsolidateRoots={handleConsolidateRoots}
            directRoles={directRoles}
            roleFocusMap={roleFocusMap}
            canEditRoleFocus={canEditRoleFocus}
            onSaveRoleFocus={handleSaveRoleFocus}
            mentionItems={mentionItems}
            circles={circles}
            projects={projects}
            onSelectCircle={handleCircleClick}
            selectedEmployeeId={selectedEmployeeId}
            onSelectEmployee={handleSelectEmployee}
            onClearEmployee={() => setSelectedEmployeeId(null)}
            onOpenProject={(projectId) => navigate(`/project/${projectId}`)}
            projectsInScope={projectsInScope}
            canViewProjects={canViewSelectedCircleProjects}
            onExportOrganization={handleExportOrganization}
            organizationExporting={organizationExporting}
            metricsRange={metricsRange}
            metricsRangeValid={metricsRangeInfo.isValid}
            onMetricsRangeChange={setMetricsRange}
            currentSlotRange={currentSlotRange}
            isUsingSlotRange={isUsingSlotRange}
            circleMetricsSummary={circleMetricsSummary}
            circleMetricsLoading={circleMetricsLoading}
            circleMetricsError={projectMetricsError}
          />
        ) : activeTab === 'insights' ? (
          <div className="flex-1 min-h-0 overflow-y-auto">
            <CircleInsightsPanel
              selectedCircle={selectedCircle}
              metricsRange={metricsRange}
              metricsRangeValid={metricsRangeInfo.isValid}
              onMetricsRangeChange={setMetricsRange}
              currentSlotRange={currentSlotRange}
              isUsingSlotRange={isUsingSlotRange}
              summary={circleMetricsSummary}
              loading={circleMetricsLoading}
              error={projectMetricsError}
              hoursHierarchy={hoursHierarchy}
              expensesHierarchy={expensesHierarchy}
            />
          </div>
        ) : activeTab === 'rules' ? (
          <RulesPanel
            rules={orgRules}
            canEdit={canEditRules}
            loading={rulesLoading}
            error={rulesError}
            focusRuleId={focusRuleId}
            onCreate={createRule}
            onUpdate={updateRule}
            onDelete={deleteRule}
            circles={circles}
            onSelectCircle={(circle) => {
              handleCircleClick(circle);
              setActiveTab('structure');
            }}
          />
        ) : activeTab === 'points' ? (
          <PointsPanel
            selectedCircle={selectedCircle}
            points={circlePoints}
            pointsLoading={circlePointsLoading}
            pointsError={circlePointsError}
            onCreatePoint={handleCreateCirclePoint}
            onDeletePoint={handleDeleteCirclePoint}
            selectedCircleIsRole={isLeafRole}
          />
        ) : activeTab === 'tensions' ? (
          <TensionsPanel
            selectedCircle={selectedCircle}
            tensions={tensions}
            tensionsLoading={tensionsLoading}
            tensionError={tensionError}
            selectedTensionId={selectedTensionId}
            circles={circles}
            tensionDraft={tensionDraft}
            tensionVotes={tensionVotes}
            tensionVotesLoading={tensionVotesLoading}
            onStartTension={handleStartTension}
            onSelectTension={handleSelectTension}
            isFieldChanged={isTensionFieldChanged}
            onUpdateField={updateTensionField}
            onUpdateProposedField={updateTensionProposedField}
            onUpdateList={updateTensionList}
            onChangeAction={handleTensionActionChange}
            onDelete={handleDeleteTension}
            onPropose={handleProposeTension}
            onWithdraw={handleWithdrawProposal}
            onCancel={handleCancelTension}
            onSave={handleSaveTension}
            user={user}
            selectedCircleIsRole={isLeafRole}
          />
        ) : (
          <VotesPanel
            votes={votes}
            votesLoading={votesLoading}
            votesError={votesError}
            circles={circles}
            onRefreshVotes={fetchVotes}
            onDecision={handleVoteDecision}
          />
        )}
      </div>

      {showTensionModal && tensionDraft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto relative">
            <button
              className="absolute top-3 right-3 rounded-full p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100"
              onClick={handleCancelTension}
              aria-label="Fermer"
            >
              &#10005;
            </button>
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-700">Tension de gouvernance</p>
                <span className="text-xs text-slate-500">{selectedCircle?.name || tensionDraft.original_data?.name || 'Cercle'}</span>
              </div>
              {tensionError && <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{tensionError}</p>}
              <TensionEditor
                tensionDraft={tensionDraft}
                circles={circles}
                user={user}
                tensionVotes={tensionVotes}
                tensionVotesLoading={tensionVotesLoading}
                tensionsLoading={tensionsLoading}
                isFieldChanged={isTensionFieldChanged}
                onUpdateField={updateTensionField}
                onUpdateProposedField={updateTensionProposedField}
                onUpdateList={updateTensionList}
                onChangeAction={handleTensionActionChange}
                onDelete={handleDeleteTension}
                onPropose={handleProposeTension}
                onWithdraw={handleWithdrawProposal}
                onCancel={handleCancelTension}
                onSave={handleSaveTension}
                defaultCircleId={tensionDraft?.circle_id ?? selectedCircle?.id}
              />
            </div>
          </div>
        </div>
      )}
      {meetingHistoryModal}
    </div>
    </OrgMentionsContext.Provider>
  );
};

export default HolacracyPage;
